import stepper from '@src/components/Stepper/themes';
import accordion from '@src/components/Accordion/themes';
import steps from '@src/components/Stepper/themes/steps';
import timeline from '@src/components/Timeline/next/themes';
import inputtext from '@src/components/Input/themes/inputText';
import inputnumber from '@src/components/Input/themes/numberInput';
import iconfield from '@src/components/Input/themes/iconField';
import datatable from '@src/components/Table/next/theme';
import checkbox from '@src/components/Checkbox/themes';
import datepicker from '@src/components/DatePicker/themes';
import select from '@src/components/Select/themes';
import tooltip from '@src/themes/shipbob/tooltip';
import chip from '@src/components/Chip/themes';
import button from '@src/components/Button/themes';
import radiobutton from '@src/components/RadioButton/themes';
import panel from '@src/components/Panel/themes';
import drawer from '@src/components/Drawer/themes';
import tabs from '@src/components/Tabs/themes';
import password from '@src/components/Input/themes/password';
import dialog from '@src/components/Modal/themes';
import textarea from '@src/components/Input/themes/textArea';
import autocomplete from '@src/components/AutoComplete/themes';
import multiselect from '@src/components/Multiselect/themes';

export const defaultHeight = 40;

const shipbob = {
  primitive: {
    white: '#ffffff',
    black: '#000000',
    dark: '#3d444f',
    main: '#343e4f',
    primary: {
      50: '#D9E5F0',
      500: '#2470af',
      900: '#1d5c8f',
    },
    secondary: {
      50: '#DBEBDA',
      300: '#CFEACA',
      500: '#31991e',
      900: '#277918',
    },
    warning: {
      50: '#fdefd6',
      500: '#e59209',
    },
    error: {
      50: '#F2DEDE',
      500: '#8e1c1c',
    },
    disabled: '#a8abb1',
    grayScale: {
      0: '#343e4f',
      100: '#f9fafb',
      200: '#e2e6ec',
      300: '#d3d9e2',
      400: '#c4cbd8',
      500: '#b4bece',
      600: '#95a3ba',
      700: '#7788a5',
      800: '#5c6e8d',
      900: '#48566e',
    },
    fonts: {
      familySansSerif: 'proxima-nova, sans-serif',
      familyBase: 'proxima-nova, sans-serif',
      default: 'proxima-nova, sans-serif, SFMono-Regular',
      heading: 'proxima-nova, sans-serif, SFMono-Regular',
      text: 'proxima-nova, sans-serif, SFMono-Regular',
    },
    sizes: {
      xxs: '0.1875rem',
      xs: '0.688rem',
      s: '0.875rem',
      m: '1rem',
      ml: '1.5rem',
      l: '2.125rem',
      xl: '2.25rem',
      xxl: '3rem',
      xxxl: '4rem',
    },
    weights: {
      light: 'lighter',
      normal: 'normal',
      semiBold: 600,
      bold: 'bold',
    },
    spacing: {
      xxxs: '0.125rem',
      xxs: '0.25rem',
      xs: '0.5rem',
      ss: '0.75rem',
      s: '1rem',
      m: '1.5rem',
      ml: '2rem',
      l: '3rem',
      xl: '4rem',
      xxl: '8rem',
    },
    lineHeights: {
      xs: 1,
      s: 1.3,
      m: 1.5,
    },
    borderRadius: {
      xs: '0.125rem',
      s: '0.188rem',
      m: '0.25rem',
      l: '0.375rem',
      xl: '0.5rem',
      xxl: '0.625rem',
      full: '50%',
    },
    breakpoints: {
      xs: 0,
      s: '36rem',
      m: '48rem',
      l: '64rem',
      xl: '85.375rem',
      xxl: '93.75rem',
    },
    zindex: {
      modal: 1000,
      appOverlay: 850,
      sidenav: 800,
      dropdown: 600,
      drawer: 500,
      default: 200,
    },
  },
  semantic: {
    transitionDuration: '0.2s',
    disabledOpacity: '0.6',
    iconSize: '{sizes.s}',
    overlay: {
      popover: {
        shadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.1)',
        padding: '{spacing.m}',
        borderRadius: '{borderRadius.m}',
      },
      select: {
        borderRadius: '{borderRadius.s}',
        shadow: '0 1px 24px 2px rgba(27,23,59,.05),0 1px 4px 0 rgba(27,23,59,.11),0 17px 12px -12px rgba(27,23,59,.28)',
      },
    },
    content: {
      borderRadius: '{borderRadius.m}',
    },
    form: {
      field: {
        paddingX: '{spacing.xs}',
        paddingY: '{spacing.xs}',
        borderRadius: '{borderRadius.s}',
        height: `${defaultHeight}px`,
      },
    },
    list: {
      option: {
        padding: '{spacing.ss} {spacing.m}',
        height: `${defaultHeight}px`,
        fontWeight: 600,
      },
      optionGroup: {
        padding: '{spacing.ss} {spacing.s}',
        height: `${defaultHeight}px`,
        fontWeight: 600,
      },
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}',
          contrastColor: '{white}',
          hoverColor: '{primary.900}',
          activeColor: '{primary.50}',
          backgroundColor: '{primary.50}',
          borderColor: '{primary.50}',
        },
        secondary: {
          color: '{secondary.500}',
          contrastColor: '{white}',
          hoverColor: '{secondary.900}',
          activeColor: '{secondary.50}',
          backgroundColor: '{secondary.50}',
          borderColor: '{secondary.50}',
        },
        warning: {
          color: '{warning.500}',
          contrastColor: '{white}',
          borderColor: '{warning.50}',
          hoverColor: '{warning.50}',
          backgroundColor: '{warning.50}',
          activeColor: '{warning.50}',
        },
        error: {
          color: '{error.500}',
          contrastColor: '{white}',
          hoverColor: '{error.50}',
          borderColor: 'red',
          activeColor: '{error.50}',
          backgroundColor: '{error.50}',
        },
        disabled: {
          color: '#D8D8D8',
          contrastColor: '{white}',
          hoverColor: '{disabled}',
          borderColor: '{disabled}',
          activeColor: '{gray.scale.200}',
        },
        monochrome: {
          color: '{gray.scale.0}',
          contrastColor: '{white}',
          hoverColor: '{gray.scale.600}',
          activeColor: '{gray.scale.700}',
        },
        success: {
          color: '{secondary.500}',
          contrastColor: '{white}',
          borderColor: '{secondary.300}',
          hoverColor: '{secondary.50}',
          activeColor: '{secondary.50}',
        },
        text: {
          color: '{grayScale.800}',
          hoverColor: '{grayScale.800}',
          mutedColor: '{grayScale.800}',
          hoverMutedColor: '{grayScale.600}',
          disabledColor: '#767676',
          placeholderColor: '#767676',
        },
        content: {
          background: '{white}',
          hoverBackground: '{grayScale.300}',
          borderColor: '{grayScale.500}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}',
        },
        form: {
          field: {
            background: '{content.background}',
            borderColor: '{grayScale.500}',
            hoverBorderColor: 'rgb(164.8435114504, 176.2557251908, 195.6564885496)',
            focusBorderColor: '{grayScale.500}',
            invalidBorderColor: '{error.border.color}',
            disabledBackground: '#e6e6e6',
            color: '{content.color}',
            placeholderColor: '{text.placeholder.color}',
            iconColor: '{grayScale.700}',
            disabledColor: '{text.disabled.color}',
          },
        },
        overlay: {
          select: {
            background: '{white}',
          },
        },
        list: {
          option: {
            focusBackground: 'rgba(36, 112, 175, 0.05)',
            selectedBackground: '{primary.50}',
            selectedFocusBackground: '{primary.50}',
            color: '{gray.scale.0}',
            focusColor: '{primary.500}',
            selectedColor: '{primary.500}',
            selectedFocusColor: '{primary.500}',
          },
          optionGroup: {
            color: '{gray.scale.0}',
          },
        },
      },
    },
  },
  components: {
    accordion,
    stepper,
    steps,
    inputtext,
    inputnumber,
    timeline,
    datatable,
    checkbox,
    datepicker,
    select,
    button,
    panel,
    chip,
    drawer,
    iconfield,
    password,
    dialog,
    radiobutton,
    tabs,
    textarea,
    autocomplete,
    multiselect,
  },
  directives: {
    tooltip,
  },
};

export default shipbob;
